import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";

export default function TrustGauge({
  score = 0,
  size = 280,
  stroke = 14,
  label = "TRUST SCORE",
  modelEstimate = false,
  // Phase 7 (v1 launch cleanup): true when the backend's own
  // `overall_confidence` is "unavailable" — i.e. evidence coverage fell
  // below the same 50% measurable-scope threshold already used
  // throughout scoring_confidence.py, not a new UI-invented cutoff. The
  // numeric score is still real and still shown (never hidden, never
  // replaced) — only the color/status framing, which otherwise implies a
  // confident read, is muted so limited evidence reads as limited
  // evidence rather than as a confident verdict.
  insufficientEvidence = false,
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [display, setDisplay] = useState(0);
  const progress = useMotionValue(0);
  const offset = useTransform(progress, (v) => circumference - (v / 100) * circumference);

  useEffect(() => {
    const controls = animate(progress, clamped, {
      duration: 1.6,
      ease: [0.22, 0.61, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clamped]);

  // Insufficient evidence reuses the SAME amber (#EAB308) the rest of the
  // app already uses for "unavailable" (see ConfidenceBadge.jsx) — not a
  // new color, and never the red used for a genuinely measured weakness.
  const color = insufficientEvidence
    ? "#EAB308"
    : clamped >= 75
      ? "#6CC24A"
      : clamped >= 50
        ? "#EAB308"
        : "#EF4444";
  const status = insufficientEvidence
    ? "Limited evidence"
    : clamped >= 85
      ? "Trusted"
      : clamped >= 65
        ? "Improving"
        : clamped >= 45
          ? "At risk"
          : "Losing trust";

  return (
    <div
      className="flex flex-col items-center"
      data-testid="trust-gauge"
      data-score={clamped}
      data-insufficient-evidence={insufficientEvidence ? "true" : "false"}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#333C47"
            strokeWidth={stroke}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="butt"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="pp-label text-[10px]">{label}</div>
          <div className="metric font-bold text-white leading-none mt-2" style={{ fontSize: size * 0.34 }}>
            {display}
          </div>
          <div className="metric text-[#C7CBD1] text-sm mt-1">/ 100</div>
          <div
            className="mt-3 px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase"
            style={{ color, border: `1px solid ${color}55`, background: `${color}0F` }}
          >
            {status}
          </div>
        </div>
      </div>
      {(modelEstimate || insufficientEvidence) && (
        <div
          className="mt-4 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold tracking-wider uppercase border"
          style={{
            color: "#EAB308",
            borderColor: "#EAB30855",
            background: "#EAB3081A",
          }}
          data-testid="gauge-model-estimate"
          title={
            insufficientEvidence
              ? "Not enough evidence was available this run to confidently characterize this score — see Assessment Coverage below for what was and wasn't measured."
              : "This score reflects Pulse Point's deterministic scoring model. Some inputs were calculated rather than independently verified this run."
          }
        >
          <span
            className="inline-block w-1.5 h-1.5"
            style={{ background: "#EAB308" }}
          />
          {insufficientEvidence
            ? "Limited evidence — see coverage below"
            : "Model estimate — not verified"}
        </div>
      )}
    </div>
  );
}
